/**
 * The plains monsters as the 2026-10-04 client drew them (commit 9fbc89f0, apps/client/src/render/sprites.ts
 * `wolf` and `beastSet(wolf, 15, 21, 20)`) — user, 2026-10-10: "the monsters too", as in the reference picture.
 * The drawing is the old one line for line: a side-on wolf in three flat tones with a dark 1-px outline, four
 * phases of its stride. They stand in for the bestiary's own (sprites.ts `buildBeasts`), finished as they were
 * (no detail pass, no rim).
 */
import { figOutline } from './figureOutline';
import { lazyRecord } from './drawBudget';
import { box, canvas, ctx2d, ell } from './pixel';
import { FRAMES } from './motions/kit';
import type { SpriteSet } from './sprites';

/** One of the wolf's four stride phases (0–3). */
function wolf(ph: number): HTMLCanvasElement {
  const c = canvas(32, 22);
  const g = ctx2d(c);
  const fur = '#6c7594';
  const sh = '#4a5072';
  const lt = '#a9b2cc';
  const lo = [0, 1, 0, -1][ph]!;
  box(g, 9 - lo, 14, 2, 6, sh);
  box(g, 20 + lo, 14, 2, 6, sh);
  box(g, 2, 7, 5, 2, fur);
  box(g, 0, 5, 3, 2, fur);
  box(g, 0, 4, 1, 1, lt);
  ell(g, 14, 12, 8.5, 4.5, fur);
  box(g, 8, 8, 12, 1, sh);
  ell(g, 14, 14.5, 6, 1.8, lt);
  ell(g, 23, 9, 4.5, 3.8, fur);
  box(g, 25, 9, 5, 3, fur);
  box(g, 26, 11, 4, 1, lt);
  box(g, 29, 9, 1, 1, '#1a1220');
  box(g, 20, 4, 2, 3, fur);
  box(g, 21, 3, 1, 1, fur);
  box(g, 24, 4, 2, 3, fur);
  box(g, 25, 3, 1, 1, sh);
  box(g, 25, 8, 1, 1, '#ffd34a');
  box(g, 12 + lo, 15, 2, 6, fur);
  box(g, 23 - lo, 14, 2, 7, fur);
  return figOutline(c);
}

/**
 * A four-phase beast as a sprite set of `FRAMES` frames (client0's frame count): each frame shows the phase
 * of the stride it falls in, standing alternates the first and third, a strike shows the second (as before).
 */
function classicSet(draw: (ph: number) => HTMLCanvasElement, ax: number, ay: number, h: number): SpriteSet {
  const four = [0, 1, 2, 3].map((i) => draw(i));
  const walk = Array.from({ length: FRAMES }, (_, i) => four[Math.floor((i * 4) / FRAMES) % 4]!);
  return {
    idle: Array.from({ length: FRAMES }, (_, i) => four[i < FRAMES / 2 ? 0 : 2]!),
    walk,
    atk: four[1]!,
    block: four[0]!,
    cast: four[1]!,
    sit: four[0]!,
    jump: four[1]!,
    poses: {},
    ax,
    ay,
    h,
  };
}

/** The classic beasts by sprite id, each built the first time it is shown. */
export function classicBeasts(): Record<string, SpriteSet> {
  return lazyRecord({ wolf: () => classicSet(wolf, 15, 21, 20) });
}
